import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, selectCls } from '../../utils/operations';

const CYCLE = { available: 'limited', limited: 'unavailable', unavailable: 'available' };
const COLORS = { available: 'bg-eco-green text-white', limited: 'bg-eco-gold/30 text-eco-deep', unavailable: 'bg-red-100 text-red-600' };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const PartnerAvailability = ({ products, onRefresh }) => {
  const [productId, setProductId] = useState(products[0]?.id || '');
  const product = products.find((p) => p.id === productId) || products[0];
  const [month, setMonth] = useState(() => new Date());
  const [slots, setSlots] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product && products[0]) setProductId(products[0].id);
  }, [products, product]);

  useEffect(() => {
    const next = {};
    (product?.availability || []).forEach((s) => { next[s.date] = s; });
    setSlots(next);
  }, [productId, product]);

  const days = useMemo(() => {
    const year = month.getFullYear();
    const m = month.getMonth();
    const first = new Date(year, m, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const total = new Date(year, m + 1, 0).getDate();
    return [...Array(startOffset).fill(null), ...Array.from({ length: total }, (_, i) => new Date(year, m, i + 1))];
  }, [month]);

  const toggle = (date) => {
    const key = iso(date);
    const current = slots[key]?.status || 'available';
    setSlots((s) => ({ ...s, [key]: { date: key, status: CYCLE[current], capacity: s[key]?.capacity ?? null, note: s[key]?.note || '' } }));
  };

  const save = async () => {
    if (!product) return;
    setSaving(true);
    try {
      await apiFetch(`/products/${product.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ availability: Object.values(slots).sort((a, b) => a.date.localeCompare(b.date)) }) });
      toast.success('Calendário guardado');
      await onRefresh();
    } catch (err) {
      toast.error('Calendário não guardado', { description: String(err.message) });
    } finally {
      setSaving(false);
    }
  };

  if (!product) return <p className="rounded-2xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Crie primeiro um produto para gerir disponibilidade.</p>;

  return (
    <div className="eco-admin-card p-6" data-testid="partner-availability-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select data-testid="availability-product" value={product.id} onChange={(e) => setProductId(e.target.value)} className={`${selectCls} min-w-64`}>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
        </select>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-full bg-eco-cream p-2"><ChevronLeft size={16} /></button>
          <p data-testid="calendar-month" className="min-w-40 text-center text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{month.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}</p>
          <button data-testid="calendar-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-full bg-eco-cream p-2"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-7 gap-2 text-center text-[10px] font-bold uppercase tracking-wider text-eco-ink/40">
        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-2">
        {days.map((d, i) => d ? (
          <button key={iso(d)} data-testid={`calendar-day-${iso(d)}`} onClick={() => toggle(d)} className={`min-h-14 rounded-xl border text-sm font-semibold transition-colors ${COLORS[slots[iso(d)]?.status || 'available'] || 'bg-white'} border-eco-ink/10`}>
            {d.getDate()}
            <span className="block text-[9px] font-normal">{slots[iso(d)]?.status || 'available'}</span>
          </button>
        ) : <span key={`empty-${i}`} />)}
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-eco-ink/50">Clique para alternar: available → limited → unavailable. Datas sem registo são consideradas sob consulta.</p>
        <button data-testid="availability-save" onClick={save} disabled={saving} className="inline-flex min-h-11 items-center gap-2 rounded-[10px] bg-eco-green px-5 text-sm font-semibold text-white transition-colors hover:bg-eco-deep disabled:opacity-60"><Save size={14} /> Guardar calendário</button>
      </div>
    </div>
  );
};

export const PartnerPayouts = ({ payouts }) => (
  <div className="space-y-3" data-testid="partner-payout-list">
    {payouts.map((p) => (
      <div key={p.id} data-testid={`partner-payout-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-eco-ink/10 bg-white px-4 py-3">
        <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-eco-ink">{p.voucher_code || 'Ajuste manual'}</p><p className="text-xs text-eco-ink/50">Due {p.due_date || '—'} · {p.note}</p></div>
        <p className="eco-number text-xl leading-tight font-bold text-eco-green">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(p.partner_amount_eur)}</p>
        <span className="rounded-full bg-eco-cream px-3 py-1 text-[11px] font-bold text-eco-ink/60">{p.status}</span>
      </div>
    ))}
    {payouts.length === 0 && <p className="rounded-2xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem payouts no ledger.</p>}
  </div>
);
